import {
  ChooseOneValue,
  GROUP_NOTE_PREFIX,
  GroupValue,
  OptionGroupDef,
  RowTextValue,
  SummaryLine,
  buildFreeTextEntry,
  buildRowTextEntries,
  isGroupValue,
  joinNestedPicks,
  toNarrativeCase,
} from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/readmission-f2f";
import { NOTE_TRAILING_DOT } from "./initial-f2f-constant";
import { CodeItem, NoteLine } from "./initial-f2f-types";
import { formatCode } from "./initial-f2f-utils";

export const stripTrailingDot = (text: string): string =>
  text.replace(NOTE_TRAILING_DOT, "").trim();

/** Summary entries as report lines, one per entry, without the sentence dot. */
export const entriesToLines = (entries: SummaryLine[]): NoteLine[] =>
  entries.flatMap((entry) => {
    if (typeof entry === "string") {
      const text = stripTrailingDot(entry);
      return text ? [{ text }] : [];
    }
    const text = stripTrailingDot(entry.text);
    if (!text && !entry.title) return [];
    return [
      { title: entry.title, text, ...(entry.isNested && { nested: true }) },
    ];
  });

const groupPicks = (value: GroupValue): string[] => {
  const picks = value.selected.map((opt) => {
    const detail = value.details[opt]?.trim();
    const label = toNarrativeCase(opt);
    return detail ? `${label} (${detail})` : label;
  });
  if (!picks.length) return [value.freeText.trim()].filter(Boolean);
  return [...picks, ...groupNote(value.freeText)];
};

const groupNote = (freeText: string): string[] => {
  const free = freeText.trim();
  return free ? [`${GROUP_NOTE_PREFIX}${free}`] : [];
};

const chooseOnePicks = (value: ChooseOneValue): string[] => {
  const detail = value.detail?.trim();
  const label = toNarrativeCase(value.selected);
  return [
    detail ? `${label} (${detail})` : label,
    ...groupNote(value.freeText),
  ].filter(Boolean);
};

/** One line per selected option so the report prints a bullet per selection under the group title. */
export const buildGroupLines = (
  title: string,
  value: GroupValue | ChooseOneValue | undefined
): NoteLine[] => {
  if (!value) return [];
  const picks = isGroupValue(value) ? groupPicks(value) : chooseOnePicks(value);
  return picks.map((text) => ({ title, text }));
};

/** Group-aware variant: appends the selected option's helper text to the choose-one line. */
export const buildOptionGroupLines = (
  group: OptionGroupDef,
  value: GroupValue | ChooseOneValue | undefined
): NoteLine[] => {
  const lines = buildGroupLines(group.title, joinNestedPicks(group, value));
  const helper =
    value && !isGroupValue(value)
      ? group.optionHelperText?.[value.selected]
      : undefined;
  if (!helper || !lines.length) return lines;
  const [first, ...rest] = lines;
  return [{ ...first, text: `${first.text} ${helper}` }, ...rest];
};

/** The typed additional note as untitled lines, closing full stop included. */
const freeTextLines = (freeText: string): NoteLine[] =>
  buildFreeTextEntry(freeText).flatMap((entry) =>
    typeof entry === "string" ? [{ text: entry }] : [entry]
  );

/** One line per code under the block label, then the additional note. */
export const buildCodeLines = (
  label: string,
  codes: CodeItem[],
  freeText: string
): NoteLine[] => [
  ...codes.map((code) => ({ title: label, text: formatCode(code) })),
  ...freeTextLines(freeText),
];

/** Checked rows as `Label: value` lines, then the additional note as an untitled "Note: …" line. */
export const buildRowLines = (
  value: RowTextValue,
  freeText: string
): NoteLine[] => [
  // An empty ticked row stays titled; untitled lines print as "Additional note".
  ...entriesToLines(
    buildRowTextEntries(value).map((entry) =>
      typeof entry === "string"
        ? { title: stripTrailingDot(entry), text: "" }
        : entry
    )
  ),
  ...groupNote(freeText).map((text) => ({ text })),
];
